import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Icon } from "./Icon";
import { COMPANY, NAV_ITEMS, RESOURCE_ITEMS, SERVICE_ITEMS } from "./data";
import { useAuth } from "./auth";
import { useCart } from "./cart";
import { MESSAGES, TECH_SERVICES } from "./content";
import { SELFPROTO } from "../laona/data";
import SelfProtoMark from "../laona/SelfProtoMark";

/* 워드마크 — i.meta® (점만 오렌지) */
export function Wordmark() {
  return (
    <span className="wordmark">
      i<span className="wordmark__dot">.</span>meta<span className="wordmark__reg">®</span>
    </span>
  );
}

/* 라우트 이동 시 스크롤 위치 초기화 (해시가 있으면 해당 요소로) */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

/* 상단 드롭다운 메뉴 (PC: 마우스를 올리면 펼침, 모바일: 들여쓴 하위 항목) */
function NavDropdown({ label, items, active, hub }) {
  if (items.length === 0) return null;
  return (
    <div className="nav__group nav__group--res">
      <button
        type="button"
        className={`nav__res-btn ${active ? "is-active" : ""}`}
        aria-haspopup="true"
      >
        {label}
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <div className="nav__dropdown nav__dropdown--res">
        {items.map((r) => (
          <NavLink
            key={r.to}
            to={r.to}
            end
            className={({ isActive }) => `nav__res-item ${isActive ? "is-active" : ""}`}
          >
            <strong>{r.label}</strong>
            <span>{r.desc}</span>
          </NavLink>
        ))}
        {hub && (
          <Link to={hub.to} className="nav__res-item nav__res-item--hub">
            <span>{hub.label}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { member, ready } = useAuth();
  const svcActive = pathname.startsWith("/services") || SERVICE_ITEMS.some((r) => pathname.startsWith(r.to));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* 페이지가 바뀌면 모바일 메뉴는 닫는다 */
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
      <div className="nav__brands">
        <Link to="/" className="nav__brand" aria-label={`${COMPANY.name} 홈`}>
          <Wordmark />
        </Link>
        {/* 라오나 키트 — 나만의 전자 장치를 만드는 /laonabot 으로 가는 버튼 (메뉴 대신 로고 옆).
            마우스를 올리거나 키보드로 고르면 아래에 서비스 설명이 나온다 */}
        <Link to="/laonabot" className="nav__selfproto" aria-describedby="nav-selfproto-tip">
          <SelfProtoMark size={22} />
          <span>{SELFPROTO.name}</span>
          <span id="nav-selfproto-tip" role="tooltip" className="nav__selfproto-tip">{SELFPROTO.desc}</span>
        </Link>
      </div>

      <nav className={`nav__links ${open ? "nav__links--open" : ""}`}>
        <NavDropdown
          label="서비스"
          items={SERVICE_ITEMS}
          active={svcActive}
          hub={{ to: "/services", label: "서비스 한눈에 보기" }}
        />

        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => (isActive ? "is-active" : "")}
          >
            {item.label}
          </NavLink>
        ))}

        <span className="nav__auth">
          {ready && (member ? (
            <NavLink
              to="/my"
              className={({ isActive }) => `nav__auth-link nav__my ${isActive ? "is-active" : ""}`}
              title={`${member.name} 님의 MY 페이지`}
            >
              MY
            </NavLink>
          ) : (
            <Link to="/login" className="nav__auth-link">로그인</Link>
          ))}
        </span>
      </nav>

      <button
        type="button"
        className={`nav__toggle ${open ? "is-open" : ""}`}
        aria-label={open ? "메뉴 닫기" : "메뉴 열기"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span /><span /><span />
      </button>
    </header>
  );
}

/* 빠른 메뉴 — PC: 화면 오른쪽에 세로로 떠서 스크롤해도 따라온다, 모바일: 화면 맨 아래 고정 바 */
function QuickBar() {
  const { count } = useCart();
  const itemClass = ({ isActive }) => `quickbar__item ${isActive ? "is-active" : ""}`;
  return (
    <nav className="quickbar" aria-label="빠른 메뉴">
      <NavLink to="/" end className={itemClass}>
        <Icon.Home width="22" height="22" aria-hidden="true" focusable="false" />
        <span>홈</span>
      </NavLink>
      <NavLink to="/cart" className={itemClass} aria-label={`장바구니 ${count}종`}>
        <span className="quickbar__icon">
          <Icon.Cart width="22" height="22" aria-hidden="true" focusable="false" />
          {count > 0 && <span className="quickbar__count">{count > 99 ? "99+" : count}</span>}
        </span>
        <span>장바구니</span>
      </NavLink>
      <NavLink to="/contact" className={({ isActive }) => `quickbar__item quickbar__item--cta ${isActive ? "is-active" : ""}`}>
        <Icon.Chat width="22" height="22" aria-hidden="true" focusable="false" />
        <span>개발 문의</span>
      </NavLink>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__brand-col">
          <div className="footer__brand"><Wordmark /></div>
          <p className="footer__line">
            {COMPANY.positioning}<br />
            {MESSAGES.support.oneTeam}
          </p>
          <p className="footer__line footer__line--dim footer__tagline">
            I.META — {COMPANY.tagline}
          </p>
          <p className="footer__line footer__line--dim">
            {COMPANY.name} {COMPANY.nameKo} · 대표 {COMPANY.ceo} · 사업자등록번호 {COMPANY.bizNo}
          </p>
          <p className="footer__line footer__line--dim">
            본사 {COMPANY.address}
          </p>
          <p className="footer__line footer__line--dim">
            {COMPANY.labLabel} {COMPANY.labAddress}
          </p>
        </div>

        <div className="footer__nav">
          <div className="footer__col">
            <h3 className="footer__col-title">서비스</h3>
            <ul>
              {SERVICE_ITEMS.map((r) => (
                <li key={r.to}><Link to={r.to}>{r.label}</Link></li>
              ))}
              <li><Link to="/process">진행 방식</Link></li>
              <li><Link to="/for">분야별 안내</Link></li>
            </ul>
          </div>

          <div className="footer__col">
            <h3 className="footer__col-title">개발 분야</h3>
            <ul>
              {TECH_SERVICES.map((t) => (
                <li key={t.slug}><Link to={`/services/${t.slug}`}>{t.label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="footer__col">
            <h3 className="footer__col-title">I.META</h3>
            <ul>
              <li><Link to="/works">개발 사례</Link></li>
              <li><Link to="/about">회사 소개</Link></li>
              {RESOURCE_ITEMS.map((r) => (
                <li key={r.to}><Link to={r.to}>{r.label}</Link></li>
              ))}
              <li><Link to="/contact">개발 문의</Link></li>
            </ul>
          </div>

          <div className="footer__col">
            <h3 className="footer__col-title">문의</h3>
            <ul>
              <li><a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
              <li><a href={`tel:${COMPANY.phone.replace(/-/g, "")}`}>{COMPANY.phone}</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="footer__bottom">
        <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
        <span className="footer__legals">
          <Link to="/terms" className="footer__legal">이용약관</Link>
          <Link to="/privacy" className="footer__legal">개인정보처리방침</Link>
        </span>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="App">
      <ScrollToTop />
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
      <QuickBar />
    </div>
  );
}
